import { route } from 'preact-router';
import { clearInflow } from '@/lib/inflow.ts';
import { signIn } from '@/store/auth.slice.ts';
import type { useAppDispatch } from '@/store/hooks.ts';
import type { DaoUser } from '@/types/dao.ts';

type AppDispatch = ReturnType<typeof useAppDispatch>;

/**
 * 로그인 마무리 — 세션 저장, 유입 경로 정리, 다음 화면으로 이동.
 *
 * 로그인 화면(팝업 로그인)과 카카오 콜백 화면(인앱 브라우저 로그인)이 **같이 쓴다.**
 * 따로 두면 한쪽만 고쳐져, 로그인한 방식에 따라 추가정보 화면을 건너뛰거나 유입
 * 경로가 남는 식으로 어긋난다. (2026-09-17)
 *
 * @throws 세션을 저장하지 못했을 때. 부르는 쪽이 사용자에게 알린다.
 */
export async function finishLogin(
  dispatch: AppDispatch,
  user: DaoUser,
  returnTo: string
): Promise<void> {
  /*
    ★ createAsyncThunk 는 dispatch 결과가 rejected 여도 예외를 던지지 않는다.
      unwrap() 없이 await 만 하면, 세션 저장이 실패했는데도 그대로 다음 화면으로
      넘어가 버린다. 그러면 /info 가 다시 비로그인으로 보고 /login 으로 돌려보내
      ?r= 이 한 겹씩 쌓이는 무한 왕복이 된다.
  */
  await dispatch(signIn(user)).unwrap();

  /*
    ★ **기록이 끝난 유입 경로는 지운다.** 서버는 유입 경로를 가입할 때 한 번만 적으므로,
      응답에 값이 있다는 것은 이 브라우저가 기억하던 코드가 제 할 일을 마쳤다는 뜻이다.
      값이 없으면 그대로 둔다 — 기존 회원이 광고 링크로 들어와 로그인만 한 경우라,
      지우면 나중에 같은 브라우저에서 가입하는 사람의 출처가 사라진다.
  */
  if (user.inflow) clearInflow();

  // 추가정보 미입력 회원은 정보 입력 화면을 먼저 거친다.
  // 백엔드는 PHP bool 을 ?int 프로퍼티에 담아 보낸다. 1 / true / "1" 로 흔들릴 수 있어 느슨하게 본다.
  if (user.requestDetail) {
    route(`/info?r=${encodeURIComponent(returnTo)}`);
  } else {
    route(returnTo);
  }
}
